import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Select, Typography } from 'antd';
import { Banner, buildOptionsFromData } from '@common/components';
import {
  ShiftTimesListQueryResponse,
  ShiftTimesListQueryVariables,
} from '@scheduling/graphql';
import { useMutation, useQuery } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CopyShiftTimesShiftsTable } from './copy-shift-times-shifts-table';
import { COPY_SHIFT_TIMES_TO_UNITS_MUTATION } from '@scheduling/graphql/mutations/copy-shift-times';
import {
  ORGANIZATION_UNITS_QUERY,
  SHIFT_TIMES_QUERY,
  Unit,
  UnitsQueryResponse,
} from '@scheduling/components';

interface Props {
  unitId: string;
  setLoading: (loading: boolean) => void;
  setDisabled: (disabled: boolean) => void;
  closeDrawer: () => void;
}

export const CopyShiftTimesToUnitsSelector = forwardRef<
  { handleConfirm: () => void },
  Props
>(({ unitId, setLoading, setDisabled, closeDrawer }, ref) => {
  const [unitsSelected, setUnitsSelected] = useState<string[]>([]);

  const { data: unitsData, loading: loadingUnits } =
    useQuery<UnitsQueryResponse>(ORGANIZATION_UNITS_QUERY);

  const { data: shiftTimes, loading: loadingShiftTimes } = useQuery<
    ShiftTimesListQueryResponse,
    ShiftTimesListQueryVariables
  >(SHIFT_TIMES_QUERY, {
    variables: {
      input: {
        organizationUnitId: unitId as string,
      },
    },
  });

  const [copyShiftTimes, { loading: loadingCopy }] = useMutation(
    COPY_SHIFT_TIMES_TO_UNITS_MUTATION,
    {
      onCompleted: () => {
        closeDrawer();

        createMessage(
          'success',
          'The Shift Times copy updates are running in the background.'
        );
      },
      onError: () => {
        createMessage('error', 'Something went wrong. Please try again.');
      },
    }
  );

  useEffect(() => {
    setLoading(loadingCopy);
  }, [setLoading, loadingCopy]);

  useImperativeHandle(ref, () => ({
    handleConfirm: () => {
      if (unitsSelected.length > 0) {
        copyShiftTimes({
          variables: {
            input: {
              fromUnitId: unitId,
              toUnitsIds: unitsSelected,
            },
          },
        });
      }
    },
  }));

  useEffect(() => {
    setDisabled(unitsSelected?.length === 0);
  }, [unitsSelected, setDisabled]);

  const units = unitsData?.organizationUnits.filter(
    (unit) => unit.id !== unitId
  );
  const unit = unitsData?.organizationUnits.find((unit) => unit.id === unitId);

  return (
    <>
      <Typography.Text>Unit / Cost Center</Typography.Text>
      <Select
        mode="multiple"
        showSearch
        allowClear
        id="selectToUnit"
        style={{ width: '100%', marginBottom: 24 }}
        loading={loadingUnits}
        options={
          units &&
          buildOptionsFromData(units, (unit: Unit) => {
            {
              return {
                label: `${unit.name} - ${unit.costCenter}`,
                value: unit.id,
              };
            }
          })
        }
        filterOption={(input, option) =>
          (option?.label?.toString() ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        onChange={(value) => {
          setUnitsSelected(value);
        }}
      />
      {unit && (
        <Banner type="warning" style={{ marginBottom: 24 }}>
          Copying {unit?.name}&apos;s shift times (shown below) to a unit will
          override any existing shift times.
        </Banner>
      )}
      <CopyShiftTimesShiftsTable
        shiftTimes={shiftTimes?.shiftTimesList.collection}
        loading={loadingShiftTimes}
      />
    </>
  );
});
